import { JSONCodec, NatsError, ErrorCode } from "nats.ws";
import type { NatsConnection, Subscription } from "nats.ws";
import type { ZodType } from "zod/v4";

import type { ApiSubscriber, ApiSubscription, HandlerContext } from "../ApiSubscriber";
import type { ConnectionContext, Subject } from "../context";
import { ApiError, ForbiddenPublisherError } from "../error";
import {
  BACKEND_ROLE,
  extractVarsAndIds,
  resolveSubject,
  wireSubject,
  withoutRole,
} from "../subjectVars";
import type { Params } from "../subjectVars";
import { keysToCamel } from "../transform";
import { fromNatsHeaders } from "./natsHeaders";

export interface SubscribeContext {
  onSubscribeError: (error: NatsError, subject: string) => void;
  onHandleError: (error: unknown, subject: string) => void;
}

export class NatsApiSubscriber implements ApiSubscriber {
  private readonly codec = JSONCodec();

  constructor(
    private connectionContext: ConnectionContext<NatsConnection>,
    private subjectVars: Record<string, string>,
    private subscribeContext: SubscribeContext,
  ) {}

  subscribe<T, P extends Params>(
    subject: Subject<P>,
    schema: ZodType<T>,
    handler: (payload: T, params: P, context: HandlerContext) => void,
    allowedRoles: readonly string[],
  ): ApiSubscription {
    const wire = wireSubject(subject, this.connectionContext.scheme);
    // NATS pins a publisher to its own role token; which roles may feed this handler is ours to decide
    const allowed = new Set([...allowedRoles, BACKEND_ROLE]);
    // a subscription spans every publisher role; the caller's own role must not pin it
    const resolved = resolveSubject(wire, withoutRole(this.subjectVars), true);
    try {
      const subscription = this.connectionContext.connection.subscribe(resolved, {
        callback: (error, msg) => {
          if (error) {
            this.subscribeContext.onSubscribeError(error, resolved);
          } else {
            try {
              const callHeaders = fromNatsHeaders(msg.headers);
              if (callHeaders.clientId === this.connectionContext.clientId) {
                return;
              }
              const event = this.codec.decode(msg.data);
              const [vars, ids] = extractVarsAndIds(wire, msg.subject);
              if (ids.role !== undefined && !allowed.has(ids.role)) {
                throw new ForbiddenPublisherError(ids.role, msg.subject);
              }
              handler(schema.parse(keysToCamel(event)), vars, { ...ids, ...callHeaders });
            } catch (err: unknown) {
              this.subscribeContext.onHandleError(err, resolved);
            }
          }
        },
      });
      return {
        getID: () => subscription.getID(),
        getSubject: () => subscription.getSubject(),
        unsubscribe: () => unsubscribe(subscription),
      };
    } catch (error) {
      throw new ApiError("Subscribe Error", `Error subscribing ${resolved}`, {
        request: { subject: resolved },
        response: { error },
      });
    }
  }
}

async function unsubscribe(subscription: Subscription): Promise<void> {
  try {
    return await subscription.drain();
  } catch (error) {
    if (error instanceof NatsError) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (error.code === ErrorCode.Disconnect) {
        return;
      }
    }
    throw new ApiError("Drain Error", "Error draining subscription", {
      request: { id: subscription.getID(), subject: subscription.getSubject() },
      response: {},
    });
  } finally {
    subscription.unsubscribe();
  }
}
